import {useCallback, useEffect, useRef, useState} from 'react';
import {agentApi} from '../api';
import {toInitials} from '../shared';

export const SOUND_KEY = 'agent_sound';

const toCaller = (m) => ({
  requestId: m.requestId,
  roomName: m.roomName,
  name: m.callerName || m.customerName || 'Customer',
  initials: toInitials(m.callerName || m.customerName || 'Customer'),
  mobile: m.callerMobile || m.customerMobile || '',
  email: m.callerEmail || m.customerEmail || null,
  propertyId: m.propertyId || null,
  property: m.propertyTitle || (m.propertyId ? `Property #${m.propertyId}` : 'General enquiry'),
  propertySlug: m.propertySlug || null,
  requestedAt: m.requestedAt || null,
});

function ringTone() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    let stopped = false;
    const ring = () => {
      if (stopped) return;
      [0, 0.25].forEach((offset) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.12, ctx.currentTime + offset);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + offset + 0.2);
        osc.connect(gain).connect(ctx.destination);
        osc.start(ctx.currentTime + offset);
        osc.stop(ctx.currentTime + offset + 0.2);
      });
    };
    ring();
    const t = setInterval(ring, 2000);
    return () => { stopped = true; clearInterval(t); ctx.close().catch(() => {}); };
  } catch {
    return () => {};
  }
}

/**
 * Customers waiting for an agent, oldest first. Filled from the socket (INCOMING_CALL adds, CALL_CLOSED removes:
 * another agent took it, or nobody did) and from /agent/waiting whenever the agent comes online, so a dashboard
 * opened mid-ring still shows them. `notice` explains why a ringing call disappeared.
 */
export function useRinging(enabled) {
  const [calls, setCalls] = useState([]);
  const [notice, setNotice] = useState('');
  const stopTone = useRef(null);

  useEffect(() => {
    if (!enabled) { setCalls([]); return; }
    agentApi.waiting().then((w) => setCalls(w.map(toCaller))).catch(() => {});
  }, [enabled]);

  const ringing = enabled && calls.length > 0;
  useEffect(() => {
    let soundOn = true;
    try { soundOn = localStorage.getItem(SOUND_KEY) !== '0'; } catch { /* storage blocked: keep the ring */ }
    if (ringing && soundOn) stopTone.current = ringTone();
    return () => { stopTone.current?.(); stopTone.current = null; };
  }, [ringing]);

  const onMessage = useCallback((m) => {
    if (m.type === 'INCOMING_CALL' || (!m.type && m.callerName)) {
      if (!enabled) return;
      setCalls((list) => (list.some((c) => c.requestId === m.requestId) ? list : [...list, toCaller(m)]));
    } else if (m.type === 'CALL_CLOSED') {
      setCalls((list) => {
        const closed = list.find((c) => c.requestId === m.requestId);
        if (closed) {
          setNotice(m.reason === 'TAKEN' ? `${closed.name}'s call was taken by ${m.takenBy || 'another agent'}.`
              : m.reason === 'MISSED' ? `${closed.name} waited 2 minutes and left. They're in Missed calls.`
                  : `${closed.name} cancelled the call.`);
        }
        return list.filter((c) => c.requestId !== m.requestId);
      });
    }
  }, [enabled]);

  const remove = useCallback((requestId) => setCalls((list) => list.filter((c) => c.requestId !== requestId)), []);

  return {calls, onMessage, remove, notice, clearNotice: () => setNotice('')};
}
